import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Building2Icon, CalendarClockIcon, CalendarDaysIcon, ClipboardCheckIcon, DollarSignIcon, ExternalLinkIcon, HashIcon, LandmarkIcon, LinkIcon, LockIcon, ShieldCheckIcon, StickyNoteIcon, XIcon } from 'lucide-react';
import { StateComplianceData, SURETY_COVERAGE_PRESETS, formatCurrencyShort, normalizeExternalUrl } from './types';
interface CompanyStateComplianceModalProps {
  stateName: string;
  isOpen: boolean;
  onClose: () => void;
  compliance: StateComplianceData;
  onUpdateCompliance: (compliance: StateComplianceData) => void;
}
interface DateFieldProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  icon: typeof CalendarDaysIcon;
  accentClass: string;
}
const FIELD_SHELL = 'flex items-center rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2.5 transition-colors focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/30';
export function CompanyStateComplianceModal({
  stateName,
  isOpen,
  onClose,
  compliance,
  onUpdateCompliance
}: CompanyStateComplianceModalProps) {
  const updateField = <K extends keyof StateComplianceData,>(field: K, value: StateComplianceData[K]) => {
    onUpdateCompliance({
      ...compliance,
      [field]: value
    });
  };
  if (!isOpen) return null;
  const bondUrl = compliance.bondCompanyUrl ? normalizeExternalUrl(compliance.bondCompanyUrl) : '';
  return <AnimatePresence data-id="element-59" data-mp="35hlso8biqyx7">
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6" data-id="element-60" data-mp="35i52f1r0mh05">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/65 backdrop-blur-sm" data-id="element-61" data-mp="2ri3zzhv3r1ik" />
        <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 320
      }} role="dialog" aria-modal="true" aria-labelledby="company-compliance-title" className="relative flex w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-emerald-500/25 bg-slate-900 shadow-2xl shadow-black/40 max-h-[90vh]" data-id="element-62" data-mp="1g5tgk2290hqs">
          <div className="flex items-start justify-between gap-4 border-b border-slate-800 bg-slate-900/80 px-5 py-4 sm:px-6" data-id="element-63" data-mp="3420ytsc6v8zq">
            <div className="flex min-w-0 items-center gap-3" data-id="element-64" data-mp="2z1n1p5c4ljgh">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-emerald-500/25 bg-emerald-500/10" data-id="element-65" data-mp="2u31x6tpucdm8">
                <Building2Icon className="h-5 w-5 text-emerald-400" data-id="element-66" data-mp="2q3b3ah50phdh" />
              </div>
              <div className="min-w-0" data-id="element-67" data-mp="sanmaelps9fb">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-400" data-id="element-68" data-mp="s9m8419ej6td">
                  <LockIcon className="h-3 w-3" data-id="element-69" data-mp="268i1ps7zm11y" />
                  Private · Olympian Mortgage
                </p>
                <h2 id="company-compliance-title" className="mt-0.5 text-xl font-bold text-white" data-id="element-70" data-mp="2b61jh23w8vyn">
                  {stateName}
                </h2>
              </div>
            </div>
            <button onClick={onClose} className="-mr-2 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400" aria-label="Close company compliance details" data-id="element-71" data-mp="2k1hhlvbolxa1">
              <XIcon className="h-5 w-5" data-id="element-72" data-mp="218vr7qjjmywu" />
            </button>
          </div>

          <div className="custom-scrollbar flex-1 overflow-y-auto p-5 sm:p-6" data-id="element-73" data-mp="216sr2doofpee">
            <div className="space-y-6" data-id="element-74" data-mp="37ofvqmcbwt4s">
              <section aria-labelledby="renewal-dates-heading" data-id="element-75" data-mp="3co286tkc25cj">
                <h3 id="renewal-dates-heading" className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400" data-id="element-76" data-mp="3cm7n9f92ad8g">
                  Renewal & audit dates
                </h3>
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-77" data-mp="3k7bkauk84i5a">
                  <DateField label="License renewal date" value={compliance.renewalDate} onChange={value => updateField('renewalDate', value)} icon={CalendarClockIcon} accentClass="text-emerald-400" data-id="element-78" data-mp="3k8wg0ni9y74q" />
                  <DateField label="Secretary of State renewal" value={compliance.secretaryOfStateRenewalDate} onChange={value => updateField('secretaryOfStateRenewalDate', value)} icon={LandmarkIcon} accentClass="text-sky-400" data-id="element-79" data-mp="3p4tsujubwvcr" />
                  <DateField label="Audit date" value={compliance.auditDate} onChange={value => updateField('auditDate', value)} icon={ClipboardCheckIcon} accentClass="text-amber-400" data-id="element-80" data-mp="bpx7olnxjfo9" />
                </div>
              </section>

              <section className="border-t border-slate-800 pt-6" aria-labelledby="bond-heading" data-id="element-81" data-mp="qdfgnw8iywgh">
                <h3 id="bond-heading" className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400" data-id="element-82" data-mp="ckzbopw2cocx">
                  Surety bond
                </h3>
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-83" data-mp="7nplv94riaok">
                  <div data-id="element-84" data-mp="7o8rprh518m8">
                    <label className="mb-2 block text-xs font-medium text-slate-400" data-id="element-85" data-mp="3ptlttjqkgs1u">
                      Bond amount
                    </label>
                    <div className={FIELD_SHELL} data-id="element-86" data-mp="3urneua7jz1fy">
                      <DollarSignIcon className="mr-2 h-4 w-4 flex-shrink-0 text-slate-500" data-id="element-87" data-mp="20cgi3xhaxz2u" />
                      <input type="number" min="0" value={compliance.suretyBondAmount ?? ''} onChange={event => updateField('suretyBondAmount', event.target.value === '' ? null : Number(event.target.value))} placeholder="0" className="w-full bg-transparent text-sm tabular-nums text-slate-100 outline-none placeholder:text-slate-600" aria-label="Surety bond amount" data-id="element-88" data-mp="20eb7bnzlwdcq" />
                    </div>
                  </div>

                  <div data-id="element-89" data-mp="twe7ldc1yc9s">
                    <label className="mb-2 block text-xs font-medium text-slate-400" data-id="element-90" data-mp="tvuymm4dqy7w">
                      Held by
                    </label>
                    <div className={FIELD_SHELL} data-id="element-91" data-mp="35lkrk5b4amlm">
                      <ShieldCheckIcon className="mr-2 h-4 w-4 flex-shrink-0 text-slate-500" data-id="element-92" data-mp="3aib78rj0fg6i" />
                      <input type="text" value={compliance.suretyBondHeldBy ?? ''} onChange={event => updateField('suretyBondHeldBy', event.target.value || null)} placeholder="Surety provider or holder" className="w-full bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-600" data-id="element-93" data-mp="3ejwenxcrz29b" />
                    </div>
                  </div>

                  <DateField label="Bond expiration date" value={compliance.bondExpirationDate} onChange={value => updateField('bondExpirationDate', value)} icon={CalendarDaysIcon} accentClass="text-rose-400" data-id="element-94" data-mp="23vpdu8aj9qlw" />

                  <div data-id="element-95" data-mp="1jyjq0u0ppdri">
                    <label className="mb-2 block text-xs font-medium text-slate-400" data-id="element-96" data-mp="1xrsxgp53np7e">
                      Bond code
                    </label>
                    <div className={FIELD_SHELL} data-id="element-97" data-mp="1trqvsfzd6vs4">
                      <HashIcon className="mr-2 h-4 w-4 flex-shrink-0 text-slate-500" data-id="element-98" data-mp="160ledwdrzvfp" />
                      <input type="text" value={compliance.bondCode ?? ''} onChange={event => updateField('bondCode', event.target.value || null)} placeholder="e.g. MI-4482-B" className="w-full bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-600" data-id="element-99" data-mp="111ryxyexhucw" />
                    </div>
                  </div>
                </div>

                <div className="mt-3" data-id="element-100" data-mp="2txktt1wyosh1">
                  <label className="mb-2 block text-xs font-medium text-slate-400" data-id="element-101" data-mp="2u0qiig1ohsc8">
                    Surety coverage amount
                  </label>
                  <div className={FIELD_SHELL} data-id="element-102" data-mp="2k19hibxdg2ml">
                    <DollarSignIcon className="mr-2 h-4 w-4 flex-shrink-0 text-slate-500" data-id="element-103" data-mp="2k2kkwamwn8d6" />
                    <input type="number" min="0" value={compliance.suretyCoverageAmount ?? ''} onChange={event => updateField('suretyCoverageAmount', event.target.value === '' ? null : Number(event.target.value))} placeholder="150000" className="w-full bg-transparent text-sm tabular-nums text-slate-100 outline-none placeholder:text-slate-600" aria-label="Surety coverage amount" data-id="element-104" data-mp="2ozszy2crawa7" />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-105" data-mp="3w2vbtcoitxj1">
                    {SURETY_COVERAGE_PRESETS.map(amount => {
                    const isActive = compliance.suretyCoverageAmount === amount;
                    return <button key={amount} onClick={() => updateField('suretyCoverageAmount', amount)} className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${isActive ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300' : 'border-slate-700/60 bg-slate-800/40 text-slate-400 hover:text-slate-200'}`} data-id="element-106" data-mp="3n52ts0rf0bry">
                          {formatCurrencyShort(amount)}
                        </button>;
                  })}
                  </div>
                </div>

                <div className="mt-3" data-id="element-107" data-mp="25uhg7dtac0n8">
                  <label className="mb-2 block text-xs font-medium text-slate-400" data-id="element-108" data-mp="25viq3eykeh3d">
                    Bond company link
                  </label>
                  <div className="flex items-center gap-2" data-id="element-109" data-mp="2frcb7mqcniwr">
                    <div className={`${FIELD_SHELL} flex-1`} data-id="element-110" data-mp="2ftykbqsw26h3">
                      <LinkIcon className="mr-2 h-4 w-4 flex-shrink-0 text-slate-500" data-id="element-111" data-mp="2tqc1eytig5v1" />
                      <input type="url" value={compliance.bondCompanyUrl ?? ''} onChange={event => updateField('bondCompanyUrl', event.target.value || null)} placeholder="https://suretycompany.com/policy" className="w-full bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-600" data-id="element-112" data-mp="2orilz0uny4s8" />
                    </div>
                    {bondUrl ? <a href={bondUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 text-xs font-medium text-emerald-300 transition-colors hover:bg-emerald-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400" data-id="element-113" data-mp="3kgq05qxdi1k4">
                        <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-114" data-mp="c4294sfl1tzb" />
                        Open
                      </a> : <span className="rounded-xl border border-slate-800 px-3 py-2.5 text-xs text-slate-600" data-id="element-115" data-mp="gzra90793ixq">
                        Open
                      </span>}
                  </div>
                </div>
              </section>

              <section className="border-t border-slate-800 pt-6" aria-labelledby="notes-heading" data-id="element-116" data-mp="zz5kl53j0kuz">
                <h3 id="notes-heading" className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400" data-id="element-117" data-mp="r6204cugr7qu">
                  <StickyNoteIcon className="h-3.5 w-3.5" data-id="element-118" data-mp="1ix83d25hdlkc" />
                  Notes
                </h3>
                <textarea value={compliance.notes ?? ''} onChange={event => updateField('notes', event.target.value || null)} rows={4} placeholder="Miscellaneous notes — filing quirks, examiner contacts, follow-ups…" className="w-full resize-y rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2.5 text-sm leading-5 text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30" data-id="element-119" data-mp="1evmt7okbzldu" />
              </section>
            </div>
          </div>

          <div className="flex justify-end border-t border-slate-800 bg-slate-900/80 px-5 py-4 sm:px-6" data-id="element-120" data-mp="1m7sweekh8kjl">
            <button onClick={onClose} className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-950/40 transition-colors hover:bg-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300" data-id="element-121" data-mp="18c71jqrkb6j9">
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}
function DateField({
  label,
  value,
  onChange,
  icon: Icon,
  accentClass
}: DateFieldProps) {
  return <div data-id="element-122" data-mp="fpw2ikjltdok">
      <label className="mb-2 block text-xs font-medium text-slate-400" data-id="element-123" data-mp="fpcsrr43xvlm">
        {label}
      </label>
      <div className={FIELD_SHELL} data-id="element-124" data-mp="koz4tx8795su">
        <Icon className={`mr-2 h-4 w-4 flex-shrink-0 ${accentClass}`} data-id="element-125" data-mp="kq0hg64v60co" />
        <input type="date" value={value ?? ''} onChange={event => onChange(event.target.value || null)} className="w-full bg-transparent text-sm text-slate-100 outline-none" style={{
        colorScheme: 'dark'
      }} data-id="element-126" data-mp="d452ujwxvy8k" />
      </div>
    </div>;
}